import {observer} from 'mobx-react-lite';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {EAutocompleteOption} from 'webapp-lib/Components';
import {useDebouncedValue} from 'webapp-lib/utils';

import i18n from '_/i18n';
import {EFAutocomplete, EFAutocompleteProps, useWatch} from '_/utilities/form';
import {PaginatedItemLoader} from '_/utilities/simple-fetch';
import {
  defaultPaginationArguments,
  PageItem,
  PaginationDirection,
  PaginationMode,
} from '_/utilities/types';

import {EFTagsAutocomplete} from './ETagsAutocomplete';
import {ContactAutocompleteProps} from './types';
import {buildSearchContacts, disableSelectedOptions} from './utils';

type ContactItem = {id: string};

const UContactAutocomplete = <
  T extends ContactItem,
  EXTRA_PARAMS extends object,
>({
  onContactSelection,
  appendCreateContactOption = false,
  filterContacts,
  loadContactCall,
  loadContactCallInitialExtraParams,
  mapToOptions,
  alreadySelectedContactIds,
  multiple = false,
  reloadOptionsTrigger = false,
  onSelectedIdMissing,
  ...otherProps
}: Omit<EFAutocompleteProps, 'options'> & {
  alreadySelectedContactIds?: string[];
  reloadOptionsTrigger?: boolean | string;
  onSelectedIdMissing?: () => void;
} & ContactAutocompleteProps<T, EXTRA_PARAMS>): React.ReactElement => {
  const [debounceSearchQuery, searchQuery, setSearchQuery] =
    useDebouncedValue('');
  const [contactListStore] = useState(
    new PaginatedItemLoader(
      buildSearchContacts(loadContactCall, loadContactCallInitialExtraParams)
    )
  );

  const selectedContactId = useWatch({
    control: otherProps.control,
    name: otherProps.name,
    defaultValue: otherProps.defaultValue,
  });

  const contactOptions = useMemo(
    () =>
      disableSelectedOptions(
        filterContacts
          ? filterContacts(contactListStore.loadable.data || []).map(
              (pageItemContact: PageItem<T>) =>
                mapToOptions(pageItemContact.item)
            )
          : (contactListStore.loadable.data || []).map(
              (pageItemContact: PageItem<T>) =>
                mapToOptions(pageItemContact.item)
            ),
        alreadySelectedContactIds ?? []
      ),
    [
      filterContacts,
      contactListStore.loadable.data,
      alreadySelectedContactIds,
      mapToOptions,
    ]
  );

  const onLoadMoreContactsBefore = useCallback(async () => {
    await contactListStore.fetchMore(
      {mode: PaginationMode.Browse, direction: PaginationDirection.Backward},
      {searchQuery}
    );
  }, [contactListStore, searchQuery]);

  const onLoadMoreContactsAfter = useCallback(async () => {
    await contactListStore.fetchMore(
      {mode: PaginationMode.Browse, direction: PaginationDirection.Forward},
      {searchQuery}
    );
  }, [contactListStore, searchQuery]);

  useEffect(() => {
    (async () => {
      if (selectedContactId && !debounceSearchQuery && !multiple) {
        await contactListStore.fetch(
          {
            mode: PaginationMode.Navigate,
            key: [selectedContactId],
          },
          {searchQuery: ''}
        );
        if (onSelectedIdMissing && selectedContactId) {
          const contact = (
            filterContacts
              ? filterContacts(contactListStore.loadable.data || [])
              : contactListStore.loadable.data
          )?.find((c) => c.item.id === selectedContactId);
          if (!contact) {
            onSelectedIdMissing();
          }
        }
      } else {
        await contactListStore.fetch(defaultPaginationArguments, {
          searchQuery: debounceSearchQuery,
        });
      }
    })();
    // Only react to changes in debounceSearchQuery, selectedContactId and reloadOptionsTrigger
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounceSearchQuery, selectedContactId, reloadOptionsTrigger]);

  const onChangeHandler = (
    e: React.ChangeEvent<any>,
    originalEventHandler: React.ChangeEventHandler
  ) => {
    if (onContactSelection) {
      const selectedContact = (contactListStore.loadable.data || []).find(
        (contact) => mapToOptions(contact.item).value === e.target.value
      );

      const selectedExtraContact =
        extraOption.value === e.target.value ? extraContactItem : null;

      const selectionWithFallbackToExtraItems =
        selectedContact?.item ?? selectedExtraContact ?? e.target.value;

      onContactSelection(selectionWithFallbackToExtraItems, searchQuery);
    }

    originalEventHandler(e);
  };

  const extraOption: EAutocompleteOption = {
    value: 'createNewContact',
    displayText: i18n.t('common:actions.createNewContact'),
    isPlaceholderOption: true,
  };

  const extraContactItem = {
    id: 'createNewContact',
  };

  if (appendCreateContactOption) {
    contactOptions.push(extraOption);
  }

  return multiple ? (
    <EFTagsAutocomplete
      {...otherProps}
      customOnChange={onChangeHandler}
      hasMoreAfter={contactListStore.loadable.hasMoreAfter}
      hasMoreBefore={contactListStore.loadable.hasMoreBefore}
      options={contactOptions}
      noOptionFiltering
      onLoadMoreAfter={onLoadMoreContactsAfter}
      onLoadMoreBefore={onLoadMoreContactsBefore}
      onSearchQueryChange={setSearchQuery}
    />
  ) : (
    <EFAutocomplete
      {...otherProps}
      customOnChange={onChangeHandler}
      hasMoreAfter={contactListStore.loadable.hasMoreAfter}
      hasMoreBefore={contactListStore.loadable.hasMoreBefore}
      options={contactOptions}
      noOptionFiltering
      onLoadMoreAfter={onLoadMoreContactsAfter}
      onLoadMoreBefore={onLoadMoreContactsBefore}
      onSearchQueryChange={setSearchQuery}
    />
  );
};

export const ContactAutocomplete = observer(UContactAutocomplete);
